import { useEffect, useRef } from 'react';

/**
 * Requests a screen wake lock while `active` is true, then releases it when
 * `active` becomes false or the component unmounts.
 *
 * Handles the case where the page becomes hidden (e.g. user switches apps) and
 * then visible again — the lock is automatically re-acquired on visibility change.
 *
 * Silently no-ops on browsers that don't support the Wake Lock API.
 */
export function useWakeLock(active: boolean) {
  const lockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let released = false;

    const acquire = async () => {
      try {
        lockRef.current = await navigator.wakeLock.request('screen');
        lockRef.current.addEventListener('release', () => {
          lockRef.current = null;
        });
      } catch {
        // Permission denied or not supported — silently ignore
      }
    };

    // Re-acquire when the page becomes visible again (e.g. after app switch)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !released && !lockRef.current) {
        acquire();
      }
    };

    acquire();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      lockRef.current?.release().catch(() => {});
      lockRef.current = null;
    };
  }, [active]);
}
